import { Tooltip } from "antd";
import PaginatedDailySummaryTable from "./PaginatedDailySummaryTable";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { dailySummaryProp } from "../type";

interface DailySummaryProps {
    report: any;
}

function formatDateToDayMonthShort(dateString: string): string {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' };
    return date.toLocaleDateString('en-US', options);
}

const DailySummary: React.FC<DailySummaryProps> = ({ report }) => {
    const { t } = useTranslation();
    const [dailySummary, setDailySummary] = useState<dailySummaryProp[]>();

    useEffect(() => {
        if (report && report.daily_summary) {
            // copy first: reverse() would flip the shared report data on every update
            const summaryData = [...report.daily_summary].reverse();
            setDailySummary(summaryData);
        }
    }, [report]);

    return (
        <div className="bg-background-secondary pdf-section">
            <div className="_summary_1na7g_58 mt-4 px-0 py-3 sm:p-6">
                <div className="h-full w-full rounded bg-background-secondary" data-testid="daily-summary-container" data-sentry-component="DailySummary" data-sentry-source-file="DailySummary.tsx">
                    <div className='list-tb1-fix box-border flex max-h-[380px] w-full min-w-0 flex-col overflow-y-auto rounded-1 border border-cts-gray-200'>
                        <div className="flex items-center justify-between p-3 sm:p-4 pb-1">
                            <div className="flex items-center gap-1">
                                <h6 className="m-0 p-0 text-headings-h6">{t('daily_summary')}</h6>
                                <Tooltip className="text-content-secondary" title={t('daily_summary_tooltip')}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-sentry-element="InfoIcon" data-sentry-source-file="DailySummary.tsx">
                                        <g clipPath="url(#clip0_17_307)">
                                            <path d="M13.2271 10.4172H10.7731V16.6748H13.2271V10.4172Z" fill="currentColor"></path>
                                            <path
                                                d="M12.0001 8.55216C12.3191 8.55216 12.6381 8.41719 12.8712 8.19633C13.1044 7.9632 13.2271 7.65645 13.2271 7.32516C13.2271 7.00614 13.0921 6.68713 12.8712 6.454C12.4172 6.00001 11.5952 6.00001 11.1412 6.454C10.908 6.68713 10.7731 7.00614 10.7731 7.32516C10.7731 7.64418 10.908 7.9632 11.1289 8.19633C11.362 8.41719 11.681 8.55216 12.0001 8.55216Z"
                                                fill="currentColor"
                                            ></path>
                                            <path
                                                d="M12 0C5.3865 0 0 5.3865 0 12C0 18.6135 5.3865 24 12 24C18.6135 24 24 18.6135 24 12C24 5.3865 18.6258 0 12 0ZM12 21.546C6.7362 21.546 2.45399 17.2638 2.45399 12C2.45399 6.7362 6.7362 2.45399 12 2.45399C17.2638 2.45399 21.546 6.7362 21.546 12C21.546 17.2638 17.2638 21.546 12 21.546Z"
                                                fill="currentColor"
                                            ></path>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_17_307"><rect width="24" height="24" fill="currentColor"></rect></clipPath>
                                        </defs>
                                    </svg>
                                </Tooltip>
                            </div>
                            {<div className="_container_4x09j_3 group-hover:bg-background-hover group-active:bg-background-primary">
                                <button type="button" aria-label="show-daily-summary-chart" className="_button_4x09j_27 _size-large_4x09j_57 _kind-with-border_4x09j_71" data-testid="display-daily-summary-chart-button">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g clipPath="url(#clip0_6_2798)">
                                            <path d="M2.59566 21.4054V0H0V24H24V21.4054H2.59566Z" fill="currentColor"></path>
                                            <path
                                                d="M4.94773 11.8576H6.86175C7.22307 11.8576 7.56485 11.6526 7.73087 11.3305L10.4945 6.01051C10.5628 5.88361 10.7484 5.89337 10.7679 5.89337C10.7972 5.89337 10.9534 5.91289 10.9925 6.01051L14.8596 15.8988C15.1721 16.709 15.9631 17.2361 16.881 17.2556C16.9006 17.2556 16.9103 17.2556 16.9299 17.2556C17.8283 17.2556 18.6193 16.7578 18.9611 15.9769L21.2852 10.7057H23.0235C23.5606 10.7057 24 10.2665 24 9.72959C24 9.19272 23.5606 8.75345 23.0235 8.75345H20.6505C20.2599 8.75345 19.9083 8.97796 19.7521 9.33914L17.174 15.1862C17.1252 15.3033 16.9592 15.3131 16.9201 15.3131C16.8615 15.3131 16.7248 15.3033 16.676 15.196L12.8089 5.29792C12.4964 4.50725 11.7542 3.9899 10.8656 3.94109C9.96714 3.89229 9.16638 4.34131 8.766 5.1027L6.27582 9.9053H4.94773C4.41063 9.9053 3.97119 10.3446 3.97119 10.8814C3.97119 11.4183 4.41063 11.8576 4.94773 11.8576Z"
                                                fill="currentColor"
                                            ></path>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_6_2798"><rect width="24" height="24" fill="currentColor"></rect></clipPath>
                                        </defs>
                                    </svg>
                                </button>
                            </div>}
                        </div>
                        <div className={`bg-trang-500`}>
                            <PaginatedDailySummaryTable dailySummary={dailySummary} formatDateToDayMonthShort={formatDateToDayMonthShort} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DailySummary;